import { supabase } from './supabase'

/**
 * User administration (027) — the half of administering people that is not
 * roles.
 *
 * Roles live in lib/roles.ts and were built first because ACC-6 asked for them.
 * This is everything the roles screen assumed somebody else was doing: getting
 * a person's access removed, finding out which address their invitation went
 * to, and seeing where they sit in a company of ~900.
 */

/** One node of the org tree, pre-flattened with its path for a picker. */
export type OrgUnitOption = {
  orgUnitId: string
  name: string
  type: string
  /**
   * 049c. The employment form offers only the business units sitting under the
   * chosen legal entity, and the honest way to know that is the parent — not a
   * prefix match on `path`, which goes wrong the day two entities start their
   * names with the same word and offers the wrong crew's units in silence.
   */
  parentId: string | null
  depth: number
  /** "ConeTec Chile → Santiago". */
  path: string
}

/**
 * The org tree, flattened.
 *
 * The `path` is why this is an RPC and not a client-side tree walk. Scoping
 * somebody to the wrong "Santiago" is a real mistake with 57 business units
 * across 12 legal entities, and a flat list of names invites it.
 */
export async function loadOrgUnits(): Promise<OrgUnitOption[]> {
  const { data, error } = await supabase.rpc('scopable_org_units')
  if (error) throw error
  return (
    (data ?? []) as {
      org_unit_id: string
      name: string
      type: string
      parent_id: string | null
      depth: number
      path: string
    }[]
  ).map((r) => ({
    orgUnitId: r.org_unit_id,
    name: r.name,
    type: r.type,
    parentId: r.parent_id,
    depth: r.depth,
    path: r.path,
  }))
}

export type PersonIdentity = {
  provider: string
  identifier: string
  isVerified: boolean
  isPrimary: boolean
  /** Whether an auth user has ever been attached — i.e. they have signed in. */
  linked: boolean
}

export type PersonDetail = {
  personId: string
  displayName: string
  locale: string
  timezone: string | null
  isActive: boolean
  membershipActive: boolean
  identities: PersonIdentity[]
  /**
   * Where the company believes it can reach them (055). NOT a sign-in address —
   * `identities` is that, and the two must not be conflated on screen or in
   * code. 880 of the 931 active staff have one of these and six have an
   * identity, so for almost everybody this is the only address the hub knows.
   *
   * Null means BambooHR carries no work address for them. 51 active staff are
   * in that position and they are most of ACC-24's worklist.
   */
  workEmail: string | null
  orgUnits: { orgUnitId: string; name: string; type: string }[]
  /**
   * 049 added the ids beside the names, because a form cannot prefill from a
   * name — and the department, which had no column before it.
   */
  employment: {
    jobTitle: string | null
    employmentStatus: string | null
    legalEntityId: string | null
    legalEntity: string | null
    primaryOrgUnitId: string | null
    primaryOrgUnit: string | null
    departmentId: string | null
    department: string | null
    managerPersonId: string | null
    manager: string | null
    startedOn: string | null
  } | null
  roles: { key: string; name: string; scopeId: string | null; scopeName: string | null }[]
}

/**
 * One person, in full.
 *
 * Deliberately per-person and not a list. `identity` still carries only
 * identity_read_self, so nobody can page through ~900 colleagues' personal
 * email addresses; reading one named person's is an act, gated on admin.roles,
 * and the reasoning is in migration 027's header. Do not "optimise" this into a
 * bulk fetch for the table.
 */
export async function loadPersonDetail(personId: string): Promise<PersonDetail> {
  const { data, error } = await supabase.rpc('person_admin_detail', {
    p_person_id: personId,
  })
  if (error) throw error

  const d = data as {
    person_id: string
    display_name: string
    locale: string
    timezone: string | null
    is_active: boolean
    membership_active: boolean
    identities: {
      provider: string
      identifier: string
      is_verified: boolean
      is_primary: boolean
      linked: boolean
    }[]
    work_email: string | null
    org_units: { org_unit_id: string; name: string; type: string }[]
    employment: {
      job_title: string | null
      employment_status: string | null
      legal_entity_id: string | null
      legal_entity: string | null
      primary_org_unit_id: string | null
      primary_org_unit: string | null
      department_org_unit_id: string | null
      department: string | null
      manager_person_id: string | null
      manager: string | null
      started_on: string | null
    } | null
    roles: { key: string; name: string; scope_id: string | null; scope_name: string | null }[]
  }

  return {
    personId: d.person_id,
    displayName: d.display_name,
    locale: d.locale,
    timezone: d.timezone,
    isActive: d.is_active,
    membershipActive: d.membership_active,
    identities: (d.identities ?? []).map((i) => ({
      provider: i.provider,
      identifier: i.identifier,
      isVerified: i.is_verified,
      isPrimary: i.is_primary,
      linked: i.linked,
    })),
    workEmail: d.work_email ?? null,
    orgUnits: (d.org_units ?? []).map((o) => ({
      orgUnitId: o.org_unit_id,
      name: o.name,
      type: o.type,
    })),
    employment: d.employment
      ? {
          jobTitle: d.employment.job_title,
          employmentStatus: d.employment.employment_status,
          legalEntityId: d.employment.legal_entity_id,
          legalEntity: d.employment.legal_entity,
          primaryOrgUnitId: d.employment.primary_org_unit_id,
          primaryOrgUnit: d.employment.primary_org_unit,
          departmentId: d.employment.department_org_unit_id,
          department: d.employment.department,
          managerPersonId: d.employment.manager_person_id,
          manager: d.employment.manager,
          startedOn: d.employment.started_on,
        }
      : null,
    roles: (d.roles ?? []).map((r) => ({
      key: r.key,
      name: r.name,
      scopeId: r.scope_id,
      scopeName: r.scope_name,
    })),
  }
}

/**
 * Suspend or reactivate somebody.
 *
 * This — not a role revoke — is how access is removed. The `employee` role
 * follows tenant membership and revoke_role refuses it outright (021), so
 * "remove this person's access" cannot be expressed as a role change at all.
 *
 * The reason is recorded on the audit event, not on the person. It is the
 * administrator's account of an act they took, which is a different thing from
 * a note about a colleague and is why it is safe to store (rule 5).
 */
export async function setMembershipActive(
  personId: string,
  active: boolean,
  reason?: string,
): Promise<void> {
  const { error } = await supabase.rpc('set_membership_active', {
    p_person_id: personId,
    p_active: active,
    p_reason: reason?.trim() || null,
  })
  if (error) throw error
}

/**
 * Grant one role to several people at once.
 *
 * One transaction: if the fortieth person is not a member, the first
 * thirty-nine roll back too. That is deliberate — a half-applied bulk grant is
 * the one nobody notices, and the error names the person who caused it.
 */
export async function assignRoleBulk(
  personIds: string[],
  roleKey: string,
  orgUnitId?: string | null,
): Promise<number> {
  const { data, error } = await supabase.rpc('assign_role_bulk', {
    p_person_ids: personIds,
    p_role_key: roleKey,
    p_org_unit_id: orgUnitId ?? null,
  })
  if (error) throw error
  return (data as number) ?? 0
}
